/**
 * `/app/widgets/$widgetRef` — `mockups/client/widget-chatbot.html` and
 * `mockups/client/widget-booking.html`.
 *
 * **Two mockups, one page, and nothing here branches on the kind of widget.** Diff
 * the two files and what differs is the unit noun, the numbers and the prose —
 * every one of which comes off the widget's own row, because "adding a widget type"
 * is a `widget_type` row and no code (`.docs/13`, and `usage.test.ts` makes the same
 * promise on the API side). An `if (widget.widget_type === …)` anywhere under this
 * folder would mean a difference that belongs in a column.
 *
 * The page is three sections, in the mockups' order:
 *
 *   1. `WidgetHeadline` — the count at hero size, the cost so far, the installation.
 *      The page's **one** `HeroFigure` lives there.
 *   2. `WidgetUsagePanel` — the shared component (`components/client/`), given this
 *      page's answer sentence and this page's buttons. It renders no hero of its own.
 *   3. `WidgetLastCycle` — the month that is actually being billed, and the one
 *      "how this works" fold.
 *   4. `WidgetConfigPanel` — what the widget is set up to do, for a kind whose type
 *      carries a config schema. Absent entirely for a kind that does not, which is
 *      how the chatbot and the booking pages stay exactly what they were.
 *
 * **The URL is the state.** `?period=` is read and written by `useSearchState`, so
 * the segmented control moves the address and the back button works. Absent, the
 * page sends no period at all and the API answers with its own current month —
 * which is what makes "the newest month the control offers" a fact from the server
 * rather than a reading of the browser's clock.
 */

import {
  PeriodSchema,
  isMetered,
  parsePeriod,
  type WidgetPageView,
  type WidgetSummaryView,
} from '@fareast/shared'
import { Link } from '@tanstack/react-router'
import { z } from 'zod'

import { useWidget, useWidgets } from '../../../api/client/widgets.js'
import { PeriodSeg } from '../../../components/client/PeriodSeg.js'
import { WidgetUsagePanel } from '../../../components/client/WidgetUsagePanel.js'
import { Button } from '../../../components/portal/Button.js'
import { Empty } from '../../../components/portal/Disclosure.js'
import { Region } from '../../../components/portal/Region.js'
import { Section, SectionHead } from '../../../components/portal/Layout.js'
import { DateText, Linkish, Ref } from '../../../components/portal/Text.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import { routeSearch, useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { hostedWorkspaceOf, type HostedWorkspace } from '../../../shell/hostedSurfaces.js'
import { WidgetConfigPanel } from './WidgetConfigPanel.js'
import { WidgetHeadline } from './WidgetHeadline.js'
import { WidgetLastCycle } from './WidgetLastCycle.js'
import {
  WidgetActions,
  WidgetCappedNotice,
  WidgetPausedNotice,
  WIDGET_CAPS_ANCHOR,
} from './WidgetPause.js'
import { nounOf, pluralOf, sentenceCase } from './text.js'

/**
 * The one search parameter this page owns.
 *
 * `period` is **optional** rather than defaulted, and that is the whole design: a
 * default would have to be computed from `new Date()` — lint-banned outside
 * `integrations/clock.ts`, and wrong anyway, because the month a client is billed in
 * is the studio's zone and not the browser's. No period means "whatever month you
 * think it is", which is a question only the API can answer.
 */
export const WidgetSearchSchema = z.object({ period: PeriodSchema.optional() })

/**
 * The route's `validateSearch`, exported so the address and the page agree.
 *
 * `.docs/08` §The SPA: "A route that owns its search uses `validateSearch`
 * instead" — a declared search parameter is one the router keeps across a
 * navigation and types for a `Link`. `useSearchState` still reads and writes it,
 * because that is the one hook that does; what this adds is the declaration.
 * A period the schema refuses falls back to none rather than to an error page:
 * that is somebody's hand-edited or stale URL, not a route that cannot render.
 *
 * `routeSearch` rather than a hand-written wrapper over `parseOrDefault`: session
 * 24 made that the one spelling every route uses, and `routes.test.tsx` walks the
 * tree asking each declaration which keys it carries.
 */
export const parseWidgetSearch = routeSearch(WidgetSearchSchema)

export type WidgetPageProps = {
  /** `wgt_chat_0e2d` — the widget this page is about. */
  readonly widgetRef: string
}

export function WidgetPage({ widgetRef }: WidgetPageProps) {
  const [search, setSearch] = useSearchState(WidgetSearchSchema)
  const widgets = useWidgets()
  const list = widgets.data

  if (list === undefined) {
    return (
      <>
        <PageTitle eyebrow="Widget" title="Widget" />
        <Empty>Loading this widget…</Empty>
      </>
    )
  }

  /*
   * The list is read before the widget, and not only for the name in the topbar.
   * It is how this page answers a **stale link**: `/app/widgets/wgt_gone` is a
   * reachable address, and asking for the widget first would make "there is no such
   * widget" a 404 thrown at the route boundary — an error panel where the session
   * file asks for one muted line and a way back. The list also carries the client's
   * current period, which is the newest month the segmented control may offer
   * whatever month is being looked at.
   */
  const known = list.widgets.find((one) => one.ref === widgetRef)
  if (known === undefined) return <WidgetGone widgetRef={widgetRef} />

  return (
    <WidgetScreen
      widgetRef={widgetRef}
      name={known.name}
      unitNoun={known.unit_noun}
      workspace={hostedWorkspaceOf(known.hosted_by)}
      hosted={known.hosted_by !== null}
      latest={list.summary.period}
      period={search.period}
      onPeriod={(next) => {
        setSearch({ period: parsePeriod(next) })
      }}
    />
  )
}

/** A widget this account does not have. One line, and a way back (P7). */
function WidgetGone({ widgetRef }: { readonly widgetRef: string }) {
  return (
    <>
      <PageTitle eyebrow="Widget" title="Widget" />
      <Empty>
        There is no widget {widgetRef} on this account.{' '}
        <Linkish asChild>
          <Link to="/app">Back to your dashboard</Link>
        </Linkish>
      </Empty>
    </>
  )
}

type WidgetScreenProps = {
  readonly widgetRef: string
  readonly name: string
  /** The widget's own unit noun, singular. It is what the subtitle is built from. */
  readonly unitNoun: string
  /**
   * Where a widget the portal hosts is used — a blog's posts — chosen by the server's
   * `hosted_by` in `shell/hostedSurfaces.ts`, never by the type code. Null for a widget
   * that runs as its own service, and for a surface whose workspace is not built yet.
   */
  readonly workspace: HostedWorkspace | null
  /**
   * True for a widget the portal hosts (`hosted_by`, the server's statement — never the
   * type code). Such a widget has a Connect page beside this one (session 46), and the
   * header says so.
   */
  readonly hosted: boolean
  /** The client's current month, from the list. The newest button on the control. */
  readonly latest: string
  /** The month the address asks for, if it asks for one. */
  readonly period: string | undefined
  readonly onPeriod: (period: string) => void
}

function WidgetScreen({
  widgetRef,
  name,
  unitNoun,
  workspace,
  hosted,
  latest,
  period,
  onPeriod,
}: WidgetScreenProps) {
  const format = useFormat()
  const page = useWidget(widgetRef, period)
  const shown = period ?? latest

  return (
    <>
      {/*
       * `mockups/assets/shell.js` publishes this page's own topbar strings:
       * eyebrow "Widget · August 2026", title the widget's name, subtitle
       * "Conversations handled on your site". The month is in the eyebrow because
       * the month is the one thing the segmented control beside it changes, and the
       * subtitle is the unit noun because the site is already stated once, in
       * "Embedded at" (DESIGN.md P2 — state a fact once per page).
       *
       * The mockups' verbs differ — "handled" for the chatbot, "taken" for the
       * booking widget — and no field carries one, so the neutral verb is the same
       * one the hero caption uses. `widget_type.unit_verb` is reported as missing.
       */}
      <PageTitle
        eyebrow={`Widget · ${format.monthLong(shown)}`}
        title={name}
        sub={`${sentenceCase(pluralOf(unitNoun))} recorded on your site`}
        actions={
          <>
            <PeriodSeg value={shown} latest={latest} onChange={onPeriod} />
            {/*
              Session 46: a widget the portal hosts is used in its workspace, and this page
              is its meter. The words and the address are the surface's own, from the one
              table; this page names no surface.
            */}
            {workspace === null ? null : (
              <Button size="sm" variant="ochre" asChild>
                <Link to={workspace.clientHref(widgetRef)}>{workspace.open}</Link>
              </Button>
            )}
            {/*
              Session 46: a hosted widget's keys, endpoints and site hooks are on its own
              Connect page, one for every surface — so the action is chosen by `hosted_by`
              being set, not by which surface it is.
            */}
            {hosted ? (
              <Button size="sm" asChild>
                <Link to="/app/widgets/$widgetRef/connect" params={{ widgetRef }}>
                  Connect your site
                </Link>
              </Button>
            ) : null}
            <Button size="sm" asChild>
              <Link to="/app/settings" hash={WIDGET_CAPS_ANCHOR}>
                Caps
              </Link>
            </Button>
          </>
        }
      />

      {page.data === undefined ? (
        <Empty>Loading {format.monthLong(shown)}…</Empty>
      ) : (
        <WidgetBody data={page.data} />
      )}
    </>
  )
}

type WidgetBodyProps = {
  readonly data: WidgetPageView
}

function WidgetBody({ data }: WidgetBodyProps) {
  const format = useFormat()
  const { widget, summary, series } = data

  return (
    <>
      {/*
       * At most one, and paused wins: `summary.state` is `paused` for a paused
       * widget too, and two notices saying the same site is not serving is the
       * chrome P7 deletes.
       */}
      {widget.status === 'paused' ? (
        <WidgetPausedNotice widget={widget} />
      ) : summary.state === 'limit_reached' ? (
        <WidgetCappedNotice widget={widget} summary={summary} />
      ) : null}

      <WidgetHeadline widget={widget} summary={summary} />

      <Section>
        {/* "1–24 August 2026" in the mockups: the month long, as every caption on
            this page now prints it. The 1–24 is `days_elapsed` and is stated once,
            under the hero, rather than twice in two spellings. */}
        <SectionHead title="This month" note={format.monthLong(summary.period)} pushNote />
        <Region>
          <WidgetUsagePanel
            widget={widget}
            summary={summary}
            series={series}
            answer={allowanceAnswer(summary, format)}
            actions={<WidgetActions widget={widget} />}
            foot={<WidgetFoot widget={widget} />}
          />
        </Region>
      </Section>

      <WidgetLastCycle summary={summary} />

      {/* Nothing at all for a widget whose kind takes no configuration, which is
          what the chatbot and the booking widget are. */}
      <WidgetConfigPanel widget={widget} />
    </>
  )
}

/**
 * The `xs muted` line under the meter — `widget-chatbot.html`'s "wgt_chat_9f3a ·
 * API key rotated 2 Jun 2026".
 *
 * `key_rotated_at` is nullable and is null for a widget whose key has never been
 * rotated (the booking widget), so the clause appears only when there is one. The
 * booking mockup prints something else here — "Installed as part of the website
 * build", which is `installed_via_work_order` — and that field is null in the
 * recorded world, so there is nothing to link to yet; when it is not, this is the
 * one place that decides.
 */
function WidgetFoot({ widget }: { readonly widget: WidgetPageView['widget'] }) {
  return (
    <>
      <Ref value={widget.ref} />
      {widget.key_rotated_at === null ? null : (
        <>
          {' · API key rotated '}
          <DateText value={widget.key_rotated_at} />
        </>
      )}
    </>
  )
}

/**
 * The panel's opening sentence (P1), from the allowance and what is over it.
 *
 * Both mockups write the same shape — "The first N <nouns> a month are included in
 * your package. M are over it, and those are the only ones you pay for" — and the
 * second half of it is only true while something *is* over. A month inside its
 * allowance gets the sentence that is true of it instead, rather than the mockup's
 * with a zero in it.
 *
 * A widget not charged per unit has no allowance to open with (session 46c): its
 * sentence says what the month's count costs, which is nothing.
 */
function allowanceAnswer(summary: WidgetSummaryView, format: Formatter): string {
  if (!isMetered(summary.unit_amount.amount)) {
    return `${format.qty(summary.used, nounOf(summary))} so far this month — each one counted, and none of them charged.`
  }
  const noun = nounOf(summary)
  const included = `The first ${format.qty(summary.included, noun)} a month are included.`
  return summary.billable > 0
    ? `${included} ${format.number(summary.billable)} are over the allowance, and those are the ` +
        'only ones you pay for.'
    : `${included} Nothing is over the allowance yet, so this month costs you nothing.`
}
